import { IFormThingsToImprove } from 'legacy/lut/classes/models/partner360/IFormThingsToImprove'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  IKeyInitiativePostSaveContext,
  KeyInitiativeEditor,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { getPersistedKeyInitiativeGoalMode } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/helpers/keyInitiativeGoalMode'
import { useActualsData } from 'legacy/lut/features/partner360/configuration/hooks/useActualsData'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import Logger from 'core/utils/Logger'
import { Partner360FontProvider } from 'partner360/components/typography/Partner360FontProvider'
import { KeyInitiative, useGetKeyInitiativesQuery } from 'partner360/entities/keyInitiatives'
import { getKeyInitiativeProgramType } from 'partner360/features/keyInitiativeEditor/lib/getKeyInitiativeProgramType'
import { INITIATIVES_EDITOR_TYPE_STEPS } from 'partner360/features/keyInitiativeEditor/model/editorConfig'
import { SHOW_COPY_VALIDATION_TOOLTIPS } from 'partner360/features/keyInitiatives/configs/config'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { formatMoney } from 'partner360/features/keyInitiatives/ui/helpers/formatMoney'
import { getBusinessPriorityNames } from 'partner360/features/keyInitiatives/ui/helpers/getBusinessPriorityNames'
import { BusinessPriorities } from 'partner360/features/keyInitiatives/ui/keyInitiativesTable/components/businessPriorities/BusinessPriorities'
import {
  getFinGoalTypeTranslationKey,
  getFinGoalValue,
} from 'partner360/features/keyInitiatives/ui/keyInitiativesTable/helpers/getFinGoal'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'
import { applyKeyInitiativeChanges } from 'partner360/services/keyInitiatives/applyKeyInitiativeChanges'

import { buildCopiedKeyInitiative } from '../lib/buildCopiedKeyInitiative'
import { buildCopySessionInitiative } from '../lib/buildCopySessionInitiative'
import {
  getAlreadyCopiedSourceIds,
  getCopyablePreviousYearInitiatives,
} from '../lib/getCopyablePreviousYearInitiatives'
import { getDescriptionQualityForCopy } from '../lib/getDescriptionQualityForCopy'
import { getInitiativesNeedingValidation } from '../lib/getInitiativesNeedingValidation'
import {
  getValidatedCopyInitiatives,
  isCopyValidationPending,
} from '../lib/getValidatedCopyInitiatives'
import { hasInvalidSelectedInitiatives } from '../lib/hasInvalidSelectedInitiatives'
import { mapPreviousYearInitiativeToEditorState } from '../lib/mapPreviousYearInitiativeToEditorState'
import { mapWithConcurrency } from '../lib/mapWithConcurrency'
import { validateKeyInitiativeForCopy } from '../lib/validateKeyInitiativeForCopy'
import { CopyValidationIssue, CopyValidationStatus, ICopyValidationState } from '../model/types'
import { useCopyInitiativesSession } from '../model/useCopyInitiativesSession'
import { useCopyValidationDictionaries } from '../model/useCopyValidationDictionaries'

import { ReadOnlyPeriodSelector } from './ReadOnlyPeriodSelector'

import styles from './CopyKeyInitiativesFromPreviousYearDialog.module.scss'

const DESCRIPTION_QUALITY_CONCURRENCY = 3
const { copyFromPreviousYear: tr } = TRANSLATION_KEYS

interface ICopyKeyInitiativesFromPreviousYearDialogProps {
  show: boolean
  partnerId: string
  programType: ProgramTypes
  currentFiscalYear: number
  activeCurrency: string
  actualsType: ActualsValueTypes
  onClose: () => void
  onCopied: () => void | Promise<void>
  onInitiativesCopied?: (sourceEntryIds: number[]) => void
  onDataChanged?: () => void | Promise<void>
}

interface ICopyDialogContentProps
  extends Omit<
    ICopyKeyInitiativesFromPreviousYearDialogProps,
    'show' | 'onCopied' | 'onDataChanged'
  > {
  previousFiscalYear: number
  onSave: (shouldRefreshBridges: boolean) => void
  onSavingStateChange: (isSaving: boolean) => void
}

const getStatusTranslation = (status: CopyValidationStatus): string => tr.validation[status]

const ValidationStatus = ({
  state,
  isAlreadyCopied,
}: {
  state?: ICopyValidationState
  isAlreadyCopied: boolean
}) => {
  if (isAlreadyCopied) {
    return (
      <Tooltip
        disabled={!SHOW_COPY_VALIDATION_TOOLTIPS}
        tipContent={() => <Translation text={tr.validation.alreadyCopied} />}
      >
        <i className={`kbase kbase-link ${styles['already-copied']}`} />
      </Tooltip>
    )
  }

  if (!state) {
    return null
  }

  if (state.status === 'queued' || state.status === 'validating') {
    return (
      <Tooltip
        disabled={!SHOW_COPY_VALIDATION_TOOLTIPS}
        tipContent={() => <Translation text={getStatusTranslation(state.status)} />}
      >
        <span className={styles['status-loader']}>
          <Loader dimension={{ width: 18, height: 18 }} />
        </span>
      </Tooltip>
    )
  }

  if (state.status === 'valid') {
    return (
      <Tooltip
        disabled={!SHOW_COPY_VALIDATION_TOOLTIPS}
        tipContent={() => <Translation text={tr.validation.valid} />}
      >
        <i className={`kbase kbase-check-circle-alt ${styles.valid}`} />
      </Tooltip>
    )
  }

  return (
    <Tooltip
      disabled={!SHOW_COPY_VALIDATION_TOOLTIPS}
      textAlign="left"
      viewClassName={styles['validation-tooltip']}
      tipContent={() => (
        <ul className={styles['issue-list']}>
          {state.issues.map((issue) => (
            <li key={issue}>
              <Translation
                text={tr.validation.issues[issue]}
                replaceParams={[
                  {
                    key: '{score}',
                    value: String(state.descriptionQuality?.score ?? 0),
                  },
                ]}
              />
            </li>
          ))}
        </ul>
      )}
    >
      <i className={`kbase kbase-close-circle-alt ${styles.invalid}`} />
    </Tooltip>
  )
}

const CopyDialogContent = ({
  partnerId,
  programType,
  currentFiscalYear,
  previousFiscalYear,
  activeCurrency,
  actualsType,
  onClose,
  onInitiativesCopied,
  onSave,
  onSavingStateChange,
}: ICopyDialogContentProps) => {
  const ownerId = useSelector(selectAppOwnerId)
  const { actualsValueType } = useActualsData()
  const { kiBusinessPriorities } = useSelector(selectJBPSettings)
  const validationStartedIds = useRef(new Set<number>())
  const isMounted = useRef(true)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())
  const [recentlyCopiedSourceIds, setRecentlyCopiedSourceIds] = useState<Set<number>>(
    () => new Set()
  )
  const [validation, setValidation] = useState<Record<number, ICopyValidationState>>({})
  const [isCopying, setIsCopying] = useState(false)
  const [hasCopyError, setHasCopyError] = useState(false)
  const [initiativeToEdit, setInitiativeToEdit] = useState<KeyInitiative | null>(null)
  const [copiedInitiativesForPlan, setCopiedInitiativesForPlan] = useState<KeyInitiative[]>([])
  const initiativeEditorInitialState = useMemo(
    () => (initiativeToEdit ? mapPreviousYearInitiativeToEditorState(initiativeToEdit) : undefined),
    [initiativeToEdit]
  )

  const previousYearQuery = useGetKeyInitiativesQuery({
    partnerId,
    fYear: previousFiscalYear,
    programType,
  })
  const currentYearQuery = useGetKeyInitiativesQuery({
    partnerId,
    fYear: currentFiscalYear,
    programType,
  })
  const dictionaries = useCopyValidationDictionaries({
    partnerId,
    programType,
    fYear: currentFiscalYear,
  })

  const previousInitiatives = useMemo(
    () => (previousYearQuery.currentData ?? []).filter((initiative) => Boolean(initiative.Parent)),
    [previousYearQuery.currentData]
  )
  const alreadyCopiedSourceIds = useMemo(
    () => getAlreadyCopiedSourceIds(currentYearQuery.currentData ?? [], recentlyCopiedSourceIds),
    [currentYearQuery.currentData, recentlyCopiedSourceIds]
  )
  const selectableInitiatives = useMemo(
    () => getCopyablePreviousYearInitiatives(previousInitiatives, alreadyCopiedSourceIds),
    [alreadyCopiedSourceIds, previousInitiatives]
  )
  const validSelectableInitiatives = useMemo(
    () => getValidatedCopyInitiatives(selectableInitiatives, validation),
    [selectableInitiatives, validation]
  )
  const validSelectableIds = useMemo(
    () => new Set(validSelectableInitiatives.map((initiative) => initiative.EntryId)),
    [validSelectableInitiatives]
  )
  const isValidationPending = useMemo(
    () => isCopyValidationPending(selectableInitiatives, validation),
    [selectableInitiatives, validation]
  )
  const selectedInitiatives = useMemo(
    () => selectableInitiatives.filter((initiative) => selectedIds.has(initiative.EntryId)),
    [selectableInitiatives, selectedIds]
  )
  const selectedValidCount = useMemo(
    () =>
      validSelectableInitiatives.filter((initiative) => selectedIds.has(initiative.EntryId)).length,
    [selectedIds, validSelectableInitiatives]
  )
  const hasLoadError =
    previousYearQuery.isError || currentYearQuery.isError || dictionaries.hasError
  const isLoading =
    !hasLoadError &&
    (previousYearQuery.currentData === undefined ||
      currentYearQuery.currentData === undefined ||
      dictionaries.isLoading)
  const isValidationComplete =
    selectedInitiatives.length > 0 &&
    selectedInitiatives.every((initiative) => validation[initiative.EntryId]?.status === 'valid')
  const validInitiatives = selectedInitiatives.filter(
    (initiative) => validation[initiative.EntryId]?.status === 'valid'
  )
  const hasInvalidSelection = hasInvalidSelectedInitiatives(selectedInitiatives, validation)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    // Market/settings may arrive after the initial checks. Refresh only frontend validation;
    // retain the cached Description Quality result and never send another quality request.
    setValidation((current) => {
      let next = current
      for (const initiative of selectableInitiatives) {
        const state = current[initiative.EntryId]
        if (!state || (state.status !== 'valid' && state.status !== 'invalid')) continue
        const result = validateKeyInitiativeForCopy({
          initiative,
          strategicInitiatives: dictionaries.strategicInitiatives,
          businessUnits: dictionaries.businessUnits,
          keyObjectivesForYear: dictionaries.keyObjectivesForYear,
          availableBusinessPriorities: dictionaries.availableBusinessPriorities,
          descriptionQuality: state.descriptionQuality,
          isDescriptionQualityUnavailable: state.issues.includes(
            CopyValidationIssue.DescriptionQualityUnavailable
          ),
          defaultValueType: actualsValueType,
        })
        if (result.isValid === state.isValid && result.issues.join(',') === state.issues.join(','))
          continue
        if (next === current) next = { ...current }
        next[initiative.EntryId] = { ...result, status: result.isValid ? 'valid' : 'invalid' }
      }
      return next
    })
  }, [
    actualsValueType,
    dictionaries.strategicInitiatives,
    dictionaries.businessUnits,
    dictionaries.keyObjectivesForYear,
    dictionaries.availableBusinessPriorities,
    selectableInitiatives,
    validation,
  ])

  useEffect(() => {
    setSelectedIds((ids) => {
      const next = new Set([...ids].filter((id) => validSelectableIds.has(id)))
      return next.size === ids.size ? ids : next
    })
  }, [validSelectableIds])

  const toggleInitiative = useCallback(
    (entryId: number) => {
      if (isCopying || isValidationPending || !validSelectableIds.has(entryId)) {
        return
      }

      setSelectedIds((ids) => {
        const next = new Set(ids)
        if (next.has(entryId)) {
          next.delete(entryId)
        } else {
          next.add(entryId)
        }
        return next
      })
    },
    [isCopying, isValidationPending, validSelectableIds]
  )

  const toggleAll = useCallback(() => {
    if (isValidationPending || isCopying || validSelectableInitiatives.length === 0) {
      return
    }

    setSelectedIds((ids) =>
      validSelectableInitiatives.every((initiative) => ids.has(initiative.EntryId))
        ? new Set()
        : new Set(validSelectableInitiatives.map((initiative) => initiative.EntryId))
    )
  }, [isCopying, isValidationPending, validSelectableInitiatives])

  const openInitiativeEditor = useCallback(
    (initiative: KeyInitiative) => {
      if (!isCopying) {
        setInitiativeToEdit(initiative)
      }
    },
    [isCopying]
  )

  const retryLoad = useCallback(() => {
    validationStartedIds.current.clear()
    setValidation({})
    void previousYearQuery.refetch()
    void currentYearQuery.refetch()
    void dictionaries.retry()
  }, [currentYearQuery, dictionaries, previousYearQuery])

  useEffect(() => {
    if (isLoading || hasLoadError) {
      return
    }

    const initiativesNeedingValidation = getInitiativesNeedingValidation(
      selectableInitiatives,
      validation
    ).filter((initiative) => !validationStartedIds.current.has(initiative.EntryId))

    if (initiativesNeedingValidation.length === 0) {
      return
    }

    const queuedStates: Record<number, ICopyValidationState> = {}
    initiativesNeedingValidation.forEach((initiative) => {
      validationStartedIds.current.add(initiative.EntryId)
      queuedStates[initiative.EntryId] = {
        status: 'queued',
        isValid: false,
        issues: [],
        descriptionQuality: null,
      }
    })
    setValidation((current) => ({ ...current, ...queuedStates }))

    void mapWithConcurrency(
      initiativesNeedingValidation,
      DESCRIPTION_QUALITY_CONCURRENCY,
      async (initiative) => {
        if (isMounted.current) {
          setValidation((current) => ({
            ...current,
            [initiative.EntryId]: {
              status: 'validating',
              isValid: false,
              issues: [],
              descriptionQuality: null,
            },
          }))
        }

        let descriptionQuality = null
        let isDescriptionQualityUnavailable = false

        try {
          descriptionQuality = await getDescriptionQualityForCopy(initiative)
        } catch {
          isDescriptionQualityUnavailable = true
        }

        const result = validateKeyInitiativeForCopy({
          initiative,
          strategicInitiatives: dictionaries.strategicInitiatives,
          businessUnits: dictionaries.businessUnits,
          keyObjectivesForYear: dictionaries.keyObjectivesForYear,
          availableBusinessPriorities: dictionaries.availableBusinessPriorities,
          descriptionQuality,
          isDescriptionQualityUnavailable,
          defaultValueType: actualsValueType,
        })
        const state: ICopyValidationState = {
          ...result,
          status: result.isValid ? 'valid' : 'invalid',
        }

        if (isMounted.current) {
          setValidation((current) => ({
            ...current,
            [initiative.EntryId]: state,
          }))
        }

        return state
      }
    )
  }, [
    actualsValueType,
    dictionaries.businessUnits,
    dictionaries.keyObjectivesForYear,
    dictionaries.availableBusinessPriorities,
    dictionaries.strategicInitiatives,
    hasLoadError,
    isLoading,
    selectableInitiatives,
    validation,
  ])

  const copyValidated = useCallback(async () => {
    if (isCopying || !isValidationComplete || isValidationPending) {
      return
    }

    setIsCopying(true)
    onSavingStateChange(true)
    setHasCopyError(false)
    const copiedIds: number[] = []

    for (const initiative of validInitiatives) {
      const descriptionQuality = validation[initiative.EntryId]?.descriptionQuality
      if (!descriptionQuality) {
        continue
      }

      const copyProgramType = getKeyInitiativeProgramType(
        initiative.Development ?? '',
        dictionaries.keyObjectivesForYear,
        programType
      )
      const payload = buildCopiedKeyInitiative(initiative, descriptionQuality)
      try {
        const response = await applyKeyInitiativeChanges<KeyInitiative>({
          ownerId,
          partnerId,
          programType: copyProgramType,
          fYear: currentFiscalYear,
          action: 'Create',
          data: payload,
        })

        if (response) {
          copiedIds.push(initiative.EntryId)
          onSave(payload.AddToBridges === 1)
          setCopiedInitiativesForPlan((copies) => [
            ...copies,
            buildCopySessionInitiative(initiative, payload, {
              partnerId,
              fYear: currentFiscalYear,
              programType: copyProgramType,
            }),
          ])
        }
      } catch (error) {
        Logger.error('Error copying Key Initiative from the previous year', error)
      }
    }

    if (copiedIds.length > 0) {
      const copiedIdSet = new Set(copiedIds)
      setRecentlyCopiedSourceIds((ids) => new Set([...ids, ...copiedIds]))
      onInitiativesCopied?.(copiedIds)
      setSelectedIds((ids) => new Set([...ids].filter((id) => !copiedIdSet.has(id))))
    }

    const hasFailedCopies = copiedIds.length !== validInitiatives.length
    setHasCopyError(hasFailedCopies)
    setIsCopying(false)
    onSavingStateChange(false)

    if (!hasFailedCopies) {
      onClose()
    }
  }, [
    currentFiscalYear,
    dictionaries.keyObjectivesForYear,
    isCopying,
    isValidationComplete,
    isValidationPending,
    onClose,
    onInitiativesCopied,
    onSave,
    onSavingStateChange,
    ownerId,
    partnerId,
    programType,
    validInitiatives,
    validation,
  ])

  const handleEditedInitiativeSaved = useCallback(
    (_newData: IFormThingsToImprove[], context?: IKeyInitiativePostSaveContext) => {
      if (!initiativeToEdit) {
        return
      }

      const sourceId = initiativeToEdit.EntryId
      onSave(context?.shouldRefreshBridges ?? initiativeToEdit.AddToBridges === 1)
      setCopiedInitiativesForPlan((copies) => [
        ...copies,
        buildCopySessionInitiative(initiativeToEdit, context?.savedInitiative ?? {}, {
          partnerId,
          fYear: currentFiscalYear,
          programType: context
            ? (context.savedInitiative.ProgramType ?? null)
            : getKeyInitiativeProgramType(
                initiativeToEdit.Development ?? '',
                dictionaries.keyObjectivesForYear,
                programType
              ),
        }),
      ])
      setRecentlyCopiedSourceIds((ids) => new Set([...ids, sourceId]))
      onInitiativesCopied?.([sourceId])
      setSelectedIds((ids) => new Set([...ids].filter((id) => id !== sourceId)))
      setInitiativeToEdit(null)
    },
    [
      currentFiscalYear,
      dictionaries.keyObjectivesForYear,
      initiativeToEdit,
      onInitiativesCopied,
      onSave,
      partnerId,
      programType,
    ]
  )

  return (
    <div className={styles.content}>
      <p className={styles.description}>
        <Translation
          text={tr.description}
          replaceParams={[
            { key: '{previousYear}', value: String(previousFiscalYear) },
            { key: '{currentYear}', value: String(currentFiscalYear) },
          ]}
        />
      </p>

      {hasLoadError && (
        <div className={styles.message}>
          <Translation text={tr.loadError} />
          <Button color="grey" variant="text" onClick={retryLoad}>
            <Translation text={tr.retry} />
          </Button>
        </div>
      )}

      {hasCopyError && (
        <div className={`${styles.message} ${styles.error}`}>
          <Translation text={tr.copyError} />
        </div>
      )}

      {isLoading ? (
        <div className={styles.loader}>
          <Loader dimension={{ width: 36, height: 36 }} />
        </div>
      ) : previousInitiatives.length === 0 ? (
        <div className={styles.empty}>
          <Translation
            text={tr.empty}
            replaceParams={[{ key: '{previousYear}', value: String(previousFiscalYear) }]}
          />
        </div>
      ) : (
        <div className={styles['table-wrap']}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles['selection-cell']}>
                  <Checkbox
                    checked={
                      validSelectableInitiatives.length > 0 &&
                      selectedValidCount === validSelectableInitiatives.length
                    }
                    indeterminate={
                      selectedValidCount > 0 &&
                      selectedValidCount < validSelectableInitiatives.length
                    }
                    disabled={
                      validSelectableInitiatives.length === 0 ||
                      isValidationPending ||
                      hasLoadError ||
                      isCopying
                    }
                    onChange={toggleAll}
                    label={
                      <span className={styles['sr-only']}>
                        <Translation text={tr.actions.selectAll} />
                      </span>
                    }
                  />
                </th>
                <th className={styles['validation-cell']}>
                  <Translation text={tr.columns.validation} />
                </th>
                <th>
                  <Translation text={tr.columns.name} />
                </th>
                <th>
                  <Translation text={tr.columns.description} />
                </th>
                <th>
                  <Translation text={tr.columns.strategicInitiative} />
                </th>
                <th>
                  <Translation text={tr.columns.businessPriorities} />
                </th>
                <th className={styles['period-cell']}>
                  <Translation text={tr.columns.period} />
                </th>
                <th className={styles['fin-goal-type-cell']}>
                  <Translation text={tr.columns.finGoalType} />
                </th>
                <th className={styles['fin-goal-cell']}>
                  <Translation
                    text={tr.columns.finGoal}
                    replaceParams={[
                      { key: '{actualsType}', value: actualsType },
                      { key: '{activeCurrency}', value: activeCurrency },
                    ]}
                  />
                </th>
                <th className={styles['action-cell']}>
                  <span className={styles['sr-only']}>
                    <Translation text={tr.actions.edit} />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {previousInitiatives.map((initiative) => {
                const isAlreadyCopied = alreadyCopiedSourceIds.has(initiative.EntryId)
                const isValidated = validation[initiative.EntryId]?.status === 'valid'
                const finGoalType = getPersistedKeyInitiativeGoalMode(initiative)
                const finGoal = getFinGoalValue(
                  initiative,
                  finGoalType,
                  actualsType,
                  activeCurrency
                )
                const businessPriorityNames = getBusinessPriorityNames(
                  kiBusinessPriorities,
                  initiative.BusinessPriorities
                )
                return (
                  <tr
                    key={initiative.EntryId}
                    className={isAlreadyCopied ? styles['disabled-row'] : ''}
                  >
                    <td className={styles['selection-cell']}>
                      <Checkbox
                        checked={
                          isValidated && !isAlreadyCopied && selectedIds.has(initiative.EntryId)
                        }
                        disabled={
                          !isValidated ||
                          isAlreadyCopied ||
                          isValidationPending ||
                          hasLoadError ||
                          isCopying
                        }
                        onChange={() => toggleInitiative(initiative.EntryId)}
                        label={
                          <span className={styles['sr-only']}>
                            <Translation
                              text={tr.actions.selectInitiative}
                              replaceParams={[
                                { key: '{initiativeName}', value: initiative.Name ?? '' },
                              ]}
                            />
                          </span>
                        }
                      />
                    </td>
                    <td className={styles['validation-cell']}>
                      <ValidationStatus
                        state={validation[initiative.EntryId]}
                        isAlreadyCopied={isAlreadyCopied}
                      />
                    </td>
                    <td className={styles['name-cell']}>
                      <button
                        type="button"
                        className={styles['name-link']}
                        disabled={isCopying}
                        onClick={() => openInitiativeEditor(initiative)}
                      >
                        {initiative.Name || '–'}
                      </button>
                    </td>
                    <td className={styles['description-cell']}>{initiative.Description}</td>
                    <td>{initiative.Development}</td>
                    <td>
                      <BusinessPriorities value={businessPriorityNames.join(', ')} />
                    </td>
                    <td className={styles['period-cell']}>
                      <ReadOnlyPeriodSelector period={initiative.Period} />
                    </td>
                    <td className={styles['fin-goal-type-cell']}>
                      <Translation text={getFinGoalTypeTranslationKey(finGoalType)} />
                    </td>
                    <td className={styles['fin-goal-cell']}>
                      {finGoal != null ? formatMoney(finGoal, activeCurrency) : '-'}
                    </td>
                    <td className={styles['action-cell']}>
                      <Button
                        className={styles['edit-button']}
                        variant="text"
                        color="primary"
                        disabled={isCopying}
                        onClick={() => openInitiativeEditor(initiative)}
                      >
                        <Translation text={tr.actions.edit} />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <footer className={styles.footer}>
        {hasInvalidSelection && (
          <div className={styles['validation-warning']} role={'alert'}>
            <Translation text={tr.validation.footerWarning} />
          </div>
        )}
        <Button
          color="primary"
          onClick={() => void copyValidated()}
          loading={isCopying}
          disabled={
            !isValidationComplete || isValidationPending || isLoading || hasLoadError || isCopying
          }
        >
          <Translation
            text={tr.actions.copy}
            replaceParams={[{ key: '{count}', value: String(validInitiatives.length) }]}
          />
        </Button>
        <Button color="grey" onClick={onClose} disabled={isCopying}>
          <Translation text={tr.actions.close} />
        </Button>
      </footer>
      {initiativeToEdit && (
        <KeyInitiativeEditor
          initialState={initiativeEditorInitialState}
          isDialogOpened
          isDataInsightPresent={false}
          partnerIdOverride={partnerId}
          fYearOverride={currentFiscalYear}
          programTypeOverride={programType}
          keyInitiativesFormatOverride={INITIATIVES_EDITOR_TYPE_STEPS}
          saveAsNew
          deferPostSaveRefresh
          additionalInitiativesForPlan={copiedInitiativesForPlan}
          previousEntryId={initiativeToEdit.EntryId}
          onLoadingStateChange={onSavingStateChange}
          onEditCancel={() => setInitiativeToEdit(null)}
          onUpdateFinished={handleEditedInitiativeSaved}
        />
      )}
    </div>
  )
}

export const CopyKeyInitiativesFromPreviousYearDialog = ({
  show,
  currentFiscalYear,
  ...props
}: ICopyKeyInitiativesFromPreviousYearDialogProps) => {
  const session = useCopyInitiativesSession({ show, currentFiscalYear, ...props })

  return show ? (
    <Partner360FontProvider>
      <ModalDialog
        show={show}
        onClose={session.close}
        closeOnOutsideClick={false}
        size="xl"
        classes={{
          window: styles['modal-window'],
          header: styles['modal-header'],
          headerBodyContent: styles['modal-header-content'],
          body: styles['modal-body'],
        }}
        header={() => (
          <Translation
            text={tr.title}
            replaceParams={[{ key: '{previousYear}', value: String(currentFiscalYear - 1) }]}
          />
        )}
        content={CopyDialogContent}
        contentProps={{
          ...props,
          onClose: session.close,
          onSave: session.recordSave,
          onSavingStateChange: session.setIsSaving,
          currentFiscalYear,
          previousFiscalYear: currentFiscalYear - 1,
        }}
      />
    </Partner360FontProvider>
  ) : null
}
